import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import { Grade } from '@mui/icons-material';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { useStaff } from '../contexts/StaffContext';

interface Submission {
  _id: string;
  student: { _id: string; name: string };
  assignment: { _id: string; title: string; totalPoints?: number };
  submissionText: string;
  submissionFileUpload?: {
    data?: string;
    mimeType?: string;
    filename?: string;
    originalName?: string;
  };
  status?: string;
  isGraded?: boolean;
  score?: number;
  maxScore?: number;
  feedback?: string;
  testScoreObtained?: number;
  testScoreTotal?: number;
  examScoreObtained?: number;
  examScoreTotal?: number;
  gradedBy?: { name: string };
  submittedAt: string;
  gradedAt?: string;
}

interface GradingStats {
  totalSubmissions: number;
  gradedSubmissions: number;
  pendingSubmissions: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
}

interface StaffSchool {
  _id: string;
  name?: string;
  logoUrl?: string;
  logoUpload?: {
    contentType?: string;
    data?: string | { type?: string; data?: number[] };
  };
  activeTermName?: string;
  termDurationMonths?: number;
  activeTermStartDate?: string;
  activeTermEndDate?: string;
  marksSubmissionDeadline?: string;
}

const StaffGrading: React.FC = () => {
  const { staff } = useStaff();
  const { className: selectedClassName, studentId } = useParams<{ className?: string; studentId?: string }>();
  const navigate = useNavigate();
  const staffSchool = typeof staff?.school === 'object' ? staff.school as StaffSchool : undefined;
  const getSchoolLogoSrc = () => {
    if (!staffSchool) return '';
    if (staffSchool.logoUrl) return staffSchool.logoUrl;
    const logoUpload = staffSchool.logoUpload as { contentType?: string; data?: string | { type?: string; data?: number[] } } | undefined;
    if (!logoUpload?.contentType || !logoUpload.data) return '';
    if (typeof logoUpload.data === 'string') {
      return `data:${logoUpload.contentType};base64,${logoUpload.data}`;
    }
    if (typeof logoUpload.data === 'object' && Array.isArray(logoUpload.data.data)) {
      try {
        const bytes = Uint8Array.from(logoUpload.data.data);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        return `data:${logoUpload.contentType};base64,${window.btoa(binary)}`;
      } catch {
        return '';
      }
    }
    return '';
  };

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<Submission[]>([]);
  const [stats, setStats] = useState<GradingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [openGradeDialog, setOpenGradeDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [submissionFileUrl, setSubmissionFileUrl] = useState('');
  const [gradeScore, setGradeScore] = useState('');
  const [gradeFeedback, setGradeFeedback] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'graded' | 'pending'>('all');
  const [classes, setClasses] = useState<Array<{ name: string; studentCount: number }>>([]);
  const [studentsByClass, setStudentsByClass] = useState<Record<string, Array<{ _id: string; name: string; email?: string; className?: string }>>>({});
  const [studentLoadError, setStudentLoadError] = useState<string | null>(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentTermSubmission, setStudentTermSubmission] = useState<{
    testScoreObtained?: number;
    testScoreTotal?: number;
    examScoreObtained?: number;
    examScoreTotal?: number;
  } | null>(null);
  const [testScoreObtained, setTestScoreObtained] = useState('');
  const [testScoreTotal, setTestScoreTotal] = useState('');
  const [examScoreObtained, setExamScoreObtained] = useState('');
  const [examScoreTotal, setExamScoreTotal] = useState('');
  const [studentUpdateSuccess, setStudentUpdateSuccess] = useState<string | null>(null);
  const [studentUpdateError, setStudentUpdateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<Array<{ _id: string; title: string }>>([]);
  const [staffSubjects, setStaffSubjects] = useState<Array<{ _id: string; name: string }>>([]);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);

  const filterSubmissions = useCallback(() => {
    let filtered = [...submissions];

    if (assignmentFilter) {
      filtered = filtered.filter((s) => s.assignment._id === assignmentFilter);
    }

    if (statusFilter === 'graded') {
      filtered = filtered.filter((s) => s.isGraded);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter((s) => !s.isGraded);
    }

    setFilteredSubmissions(filtered);
  }, [submissions, assignmentFilter, statusFilter]);

  useEffect(() => {
    loadSubmissions();
    loadStats();
    loadAssignments();
    loadClasses();
    loadStaffSubjects();
  }, []);

  useEffect(() => {
    if (!studentId) {
      setStudentTermSubmission(null);
      setTestScoreObtained('');
      setTestScoreTotal('');
      setExamScoreObtained('');
      setExamScoreTotal('');
      setStudentLoadError(null);
      setStudentUpdateSuccess(null);
      setStudentUpdateError(null);
      setSelectedSubject(null);
      return;
    }
    if (selectedSubject) {
      loadStudentTermSubmission(studentId, selectedSubject);
    }
  }, [studentId, selectedSubject]);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, assignmentFilter, statusFilter, filterSubmissions]);

  useEffect(() => {
    if (!selectedSubmission?.submissionFileUpload?.data || !selectedSubmission.submissionFileUpload.mimeType) {
      setSubmissionFileUrl('');
      return;
    }

    try {
      const base64Data = selectedSubmission.submissionFileUpload.data;
      const binaryString = window.atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: selectedSubmission.submissionFileUpload.mimeType });
      const url = URL.createObjectURL(blob);
      setSubmissionFileUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } catch {
      setSubmissionFileUrl('');
    }
  }, [selectedSubmission]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/staff/submissions');
      const submissionsData: Submission[] = Array.isArray(response.data) ? response.data.map((submission: any) => ({
        ...submission,
        isGraded: typeof submission.status === 'string'
          ? submission.status === 'graded'
          : Boolean(submission.score),
        score: submission.score,
        maxScore: submission.maxScore,
        feedback: submission.feedback,
        status: submission.status || (submission.score !== undefined ? 'graded' : 'submitted')
      })) : [];
      setSubmissions(submissionsData);
    } catch (err: any) {
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/staff/grading/stats');
      setStats(response.data);
    } catch {
      setStats(null);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await api.get('/staff/assignments');
      setAssignments(response.data.map((a: any) => ({ _id: a._id, title: a.title })));
    } catch {
      setAssignments([]);
    }
  };

  const loadClasses = async () => {
    try {
      const response = await api.get('/staff/quizzes-data/classes-and-students');
      const classList: string[] = Array.isArray(response.data.classes) ? response.data.classes : [];
      const studentsByClass = response.data.studentsByClass || {};
      setClasses(classList.map((className) => ({
        name: className,
        studentCount: Array.isArray(studentsByClass[className]) ? studentsByClass[className].length : 0
      })));
      setStudentsByClass(studentsByClass);
    } catch {
      setClasses([]);
      setStudentsByClass({});
    }
  };

  const loadStaffSubjects = async () => {
    try {
      const response = await api.get('/staff/subjects');
      setStaffSubjects(response.data);
    } catch {
      setStaffSubjects([]);
    }
  };

  const handleOpenGradeDialog = (submission: Submission) => {
    setSelectedSubmission(submission);
    setGradeScore(submission.score?.toString() || '');
    setGradeFeedback(submission.feedback || '');
    setOpenGradeDialog(true);
  };

  const handleCloseGradeDialog = () => {
    setOpenGradeDialog(false);
    setSelectedSubmission(null);
    setGradeScore('');
    setGradeFeedback('');
  };

  const termStartDate = staffSchool?.activeTermStartDate ? new Date(staffSchool.activeTermStartDate) : null;
  const termEndDate = staffSchool?.activeTermEndDate
    ? new Date(staffSchool.activeTermEndDate)
    : (termStartDate && staffSchool?.termDurationMonths
      ? new Date(new Date(termStartDate).setMonth(termStartDate.getMonth() + staffSchool.termDurationMonths))
      : null);

  const classStudents = selectedClassName ? studentsByClass[selectedClassName] || [] : [];
  const allStudents = Object.values(studentsByClass).flat();
  const selectedStudentInfo = studentId ? allStudents.find((student) => student._id === studentId) : null;
  const classBackUrl = selectedClassName ? `/staff/grading/class/${encodeURIComponent(selectedClassName)}` : '/staff/grading';
  const getSchoolLogo = getSchoolLogoSrc();

  const loadStudentTermSubmission = async (id: string, subjectName: string) => {
    setStudentLoading(true);
    setStudentLoadError(null);
    setStudentUpdateSuccess(null);
    setStudentUpdateError(null);

    try {
      const response = await api.get(`/staff/term-submissions/${id}?subject=${encodeURIComponent(subjectName)}`);
      const termSubmission = response.data;
      setStudentTermSubmission(termSubmission);
      setTestScoreObtained(termSubmission?.testScoreObtained?.toString() || '');
      setTestScoreTotal(termSubmission?.testScoreTotal?.toString() || '');
      setExamScoreObtained(termSubmission?.examScoreObtained?.toString() || '');
      setExamScoreTotal(termSubmission?.examScoreTotal?.toString() || '');
    } catch (err: any) {
      setStudentLoadError(err.response?.data?.message || 'Unable to load student term submission');
      setStudentTermSubmission(null);
    } finally {
      setStudentLoading(false);
    }
  };

  const handleUpdateStudentTermSubmission = async () => {
    if (!studentId || !selectedSubject) return;
    setError(null);
    setStudentUpdateError(null);
    setStudentUpdateSuccess(null);

    const payload: Record<string, any> = { subject: selectedSubject };
    const parsedTestObtained = testScoreObtained.trim() === '' ? undefined : Number(testScoreObtained);
    const parsedTestTotal = testScoreTotal.trim() === '' ? undefined : Number(testScoreTotal);
    const parsedExamObtained = examScoreObtained.trim() === '' ? undefined : Number(examScoreObtained);
    const parsedExamTotal = examScoreTotal.trim() === '' ? undefined : Number(examScoreTotal);

    if (parsedTestObtained !== undefined) {
      if (Number.isNaN(parsedTestObtained) || parsedTestObtained < 0) {
        setStudentUpdateError('Test score must be a non-negative number.');
        return;
      }
      payload.testScoreObtained = parsedTestObtained;
    }
    if (parsedTestTotal !== undefined) {
      if (Number.isNaN(parsedTestTotal) || parsedTestTotal < 0) {
        setStudentUpdateError('Test total must be a non-negative number.');
        return;
      }
      payload.testScoreTotal = parsedTestTotal;
    }
    if (parsedExamObtained !== undefined) {
      if (Number.isNaN(parsedExamObtained) || parsedExamObtained < 0) {
        setStudentUpdateError('Exam score must be a non-negative number.');
        return;
      }
      payload.examScoreObtained = parsedExamObtained;
    }
    if (parsedExamTotal !== undefined) {
      if (Number.isNaN(parsedExamTotal) || parsedExamTotal < 0) {
        setStudentUpdateError('Exam total must be a non-negative number.');
        return;
      }
      payload.examScoreTotal = parsedExamTotal;
    }

    try {
      setStudentLoading(true);
      const response = await api.put(`/staff/term-submissions/${studentId}`, payload);
      setStudentTermSubmission(response.data);
      setStudentUpdateSuccess('Student term scores updated successfully.');
    } catch (err: any) {
      setStudentUpdateError(err.response?.data?.message || 'Unable to update student scores');
    } finally {
      setStudentLoading(false);
    }
  };

  const handleSubmitGrade = async () => {
    try {
      if (!selectedSubmission) return;
      setError(null);
      setSuccess(null);

      const gradingMaxPoints = selectedSubmission.assignment?.totalPoints ?? selectedSubmission.maxScore ?? 100;
      const score = parseFloat(gradeScore);
      if (isNaN(score) || score < 0 || score > gradingMaxPoints) {
        setError(`Score must be between 0 and ${gradingMaxPoints}`);
        return;
      }

      await api.put(`/staff/submissions/${selectedSubmission._id}/grade`, {
        score,
        feedback: gradeFeedback.trim()
      });

      setSuccess('Grade submitted successfully');
      handleCloseGradeDialog();
      loadSubmissions();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit grade');
    }
  };

  const marksSubmissionDeadline = staffSchool?.marksSubmissionDeadline ? new Date(staffSchool.marksSubmissionDeadline) : null;
  const deadlinePassed = marksSubmissionDeadline ? Date.now() > marksSubmissionDeadline.getTime() : false;

  const handleSubmitTermResults = async () => {
    try {
      setError(null);
      setSuccess(null);
      setSubmitMessage(null);
      setSubmitLoading(true);

      const response = await api.post('/staff/submit-term-results');
      setSubmitMessage(response.data.message || 'Term results submitted successfully.');
      loadSubmissions();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit term results');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Assignment Grading
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', mb: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">School</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                {getSchoolLogo ? (
                  <Avatar src={getSchoolLogo} alt={staffSchool?.name || 'School Logo'} sx={{ width: 40, height: 40 }} />
                ) : (
                  <Avatar sx={{ width: 40, height: 40 }}>{staffSchool?.name ? staffSchool.name.charAt(0) : '?'}</Avatar>
                )}
                <Typography variant="h6">{staffSchool?.name || 'Your School'}</Typography>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Active Term</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>{staffSchool?.activeTermName || 'Active Term'}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Term Duration</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {staffSchool?.termDurationMonths ? `${staffSchool.termDurationMonths} month${staffSchool.termDurationMonths === 1 ? '' : 's'}` : 'Not configured'}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Term Start Date</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {termStartDate ? termStartDate.toLocaleDateString() : 'Not set'}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Term End Date</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {termEndDate ? termEndDate.toLocaleDateString() : 'Not set'}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Submission Deadline</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {marksSubmissionDeadline ? marksSubmissionDeadline.toLocaleDateString() : 'Not set'}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitTermResults}
          disabled={submitLoading || deadlinePassed}
        >
          {submitLoading ? 'Submitting term results…' : 'Submit Term Results'}
        </Button>
      {classes.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
          {classes.map((group) => (
            <Card
              key={group.name}
              variant="outlined"
              sx={{ cursor: 'pointer', p: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
              onClick={() => navigate(`/staff/grading/class/${encodeURIComponent(group.name)}`)}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Class
              </Typography>
              <Typography variant="h6">{group.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {group.studentCount} student{group.studentCount === 1 ? '' : 's'}
              </Typography>
            </Card>
          ))}
        </Box>
      )}
      {selectedClassName && !studentId && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Typography variant="h5">Students in {selectedClassName}</Typography>
            <Button variant="outlined" onClick={() => navigate('/staff/grading')}>
              Back to grading overview
            </Button>
          </Box>
          {classStudents.length === 0 ? (
            <Alert severity="info">No students were found for this class.</Alert>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
              {classStudents.map((student) => (
                <Card
                  key={student._id}
                  variant="outlined"
                  sx={{ cursor: 'pointer', p: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
                  onClick={() => navigate(`/staff/grading/class/${encodeURIComponent(selectedClassName)}/student/${student._id}`)}
                >
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Student
                  </Typography>
                  <Typography variant="h6">{student.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {student.className || selectedClassName}
                  </Typography>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
      {studentId && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Typography variant="h5">
              {selectedSubject 
                ? `Edit ${selectedSubject} scores for ${selectedStudentInfo?.name || 'student'}`
                : `Select subject for ${selectedStudentInfo?.name || 'student'}`
              }
            </Typography>
            <Button variant="outlined" onClick={() => selectedSubject ? setSelectedSubject(null) : navigate(classBackUrl)}>
              {selectedSubject ? 'Back to subjects' : 'Back to class'}
            </Button>
          </Box>

          {!selectedSubject ? (
            <Box>
              {staffSubjects.length === 0 ? (
                <Alert severity="info">
                  No subjects assigned to you. Please add subjects in the "Subjects" section first.
                </Alert>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                  {staffSubjects.map((subject) => (
                    <Card
                      key={subject._id}
                      variant="outlined"
                      sx={{ cursor: 'pointer', p: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
                      onClick={() => setSelectedSubject(subject.name)}
                    >
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Subject
                      </Typography>
                      <Typography variant="h6">{subject.name}</Typography>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <>
              {studentLoading ? (
                <Box display="flex" justifyContent="center"><Spinner /></Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {studentLoadError && <Alert severity="error">{studentLoadError}</Alert>}
                  {studentUpdateError && <Alert severity="error">{studentUpdateError}</Alert>}
                  {studentUpdateSuccess && <Alert severity="success">{studentUpdateSuccess}</Alert>}
                  {studentTermSubmission && (
                    <Typography variant="body2" color="text.secondary">
                      Loaded term scores for <strong>{selectedSubject}</strong>: Test {studentTermSubmission.testScoreObtained ?? '—'} / {studentTermSubmission.testScoreTotal ?? '—'}, Exam {studentTermSubmission.examScoreObtained ?? '—'} / {studentTermSubmission.examScoreTotal ?? '—'}
                    </Typography>
                  )}
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <TextField
                      label="Test Score Obtained"
                      type="number"
                      value={testScoreObtained}
                      onChange={(e) => setTestScoreObtained(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Test Total"
                      type="number"
                      value={testScoreTotal}
                      onChange={(e) => setTestScoreTotal(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Exam Score Obtained"
                      type="number"
                      value={examScoreObtained}
                      onChange={(e) => setExamScoreObtained(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Exam Total"
                      type="number"
                      value={examScoreTotal}
                      onChange={(e) => setExamScoreTotal(e.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Button variant="contained" color="primary" onClick={handleUpdateStudentTermSubmission} disabled={studentLoading || !studentId}>
                    Save {selectedSubject} term scores
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
        {deadlinePassed && (
          <Alert severity="warning">The term results submission deadline has passed. No further submissions are allowed.</Alert>
        )}
        {submitMessage && (
          <Alert severity="success" sx={{ alignSelf: 'center' }}>
            {submitMessage}
          </Alert>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Submissions
                </Typography>
                <Typography variant="h6">{stats.totalSubmissions}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Grades
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {stats.pendingSubmissions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Average Score
                </Typography>
                <Typography variant="h6">{stats.averageScore?.toFixed(2) || '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Filter by Assignment"
          value={assignmentFilter}
          onChange={(e) => setAssignmentFilter(e.target.value)}
          sx={{ minWidth: 200 }}
          SelectProps={{
            native: true
          }}
        >
          <option value="">All Assignments</option>
          {assignments.map((a) => (
            <option key={a._id} value={a._id}>
              {a.title}
            </option>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'graded' | 'pending')}
          sx={{ minWidth: 150 }}
          SelectProps={{
            native: true
          }}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="graded">Graded</option>
        </TextField>
      </Box>

      {/* Submissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Student</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Assignment</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Submitted</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Score</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubmissions.length > 0 ? (
              filteredSubmissions.map((submission) => (
                <TableRow key={submission._id}>
                  <TableCell sx={{ color: 'text.primary' }}>{submission.student?.name || 'Unknown'}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{submission.assignment?.title || 'Unknown'}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    {new Date(submission.submittedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    {submission.isGraded
                      ? `${submission.score ?? 0} / ${submission.assignment?.totalPoints ?? submission.maxScore ?? '—'}`
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    <Chip
                      label={submission.status === 'graded' || submission.isGraded ? 'Graded' : 'Pending'}
                      color={submission.status === 'graded' || submission.isGraded ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Grade />}
                      onClick={() => handleOpenGradeDialog(submission)}
                    >
                      {submission.isGraded ? 'Regrade' : 'Grade'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                  No submissions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Grading Dialog */}
      <Dialog open={openGradeDialog} onClose={handleCloseGradeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Grade Submission</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedSubmission && (
            <>
              <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                <strong>Student:</strong> {selectedSubmission.student?.name || 'Unknown student'}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                <strong>Assignment:</strong> {selectedSubmission.assignment?.title || 'Unknown assignment'}
              </Typography>

              <Box sx={{ mb: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  <strong>Submission:</strong>
                </Typography>
                {selectedSubmission.submissionText && (
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    {selectedSubmission.submissionText}
                  </Typography>
                )}
                {submissionFileUrl ? (
                  <Typography variant="body2" color="text.primary">
                    <a
                      href={submissionFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      View Submitted File
                    </a>
                  </Typography>
                ) : selectedSubmission.submissionFileUpload ? (
                  <Typography variant="body2" color="text.secondary">
                    Submitted file is unavailable or invalid.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No submitted file attached.
                  </Typography>
                )}
              </Box>

              <TextField
                label="Score"
                type="number"
                fullWidth
                value={gradeScore}
                onChange={(e) => setGradeScore(e.target.value)}
                margin="normal"
                inputProps={{
                  max: selectedSubmission.assignment?.totalPoints ?? selectedSubmission.maxScore ?? 100,
                  min: 0
                }}
                helperText={`Out of ${selectedSubmission.assignment?.totalPoints ?? selectedSubmission.maxScore ?? 100}`}
                FormHelperTextProps={{ sx: { color: 'text.secondary' } }}
              />
              <TextField
                label="Feedback"
                fullWidth
                multiline
                rows={4}
                value={gradeFeedback}
                onChange={(e) => setGradeFeedback(e.target.value)}
                margin="normal"
                placeholder="Provide constructive feedback for the student..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGradeDialog}>Cancel</Button>
          <Button onClick={handleSubmitGrade} variant="contained" color="primary">
            Submit Grade
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffGrading;
